import React, { useState, useEffect, useRef } from 'react';
import '../components/EditUserPage.css';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { clearPermissionCache } from '../utils/permissions';

// Define permission structure here to match API
const permissionStructure = {
  profile: [
    { name: 'Company', permissions: ['-', '-', 'profile', '-'] }
  ],
  folders: [
    { name: 'Master Folder', permissions: ['masterview', '-', '-', '-'] },
    { name: 'Orders Folder', permissions: ['ordersview', '-', '-', '-'] },
    // dont remove comment it -> leadview is now active in folders
    { name: 'Lead Management', permissions: ['leadview', '-', '-', '-'] },
    { name: 'Weta Folder', permissions: ['wetaorder', '-', '-', '-'] },
    { name: 'Ageing Folder', permissions: ['ageingsearch', '-', '-', '-'] },
    { name: 'Notepad Folder', permissions: ['notepadview', '-', '-', '-'] }
  ],
  master: [
    { name: 'User', permissions: ['user', 'adduser', 'edituser', '-'] },
    { name: 'Diagnosis', permissions: ['diagnosis', 'adddiagnosis', 'editdiagnosis', 'deletediagnosis'] },
    { name: 'Products', permissions: ['product', 'addproduct', 'editproduct', '-'] },
    { name: 'Category', permissions: ['category', 'addcategory', 'editcategory', '-'] }
  ],
  operation: [
    { name: 'Patient Entry', permissions: ['patient', 'addpatiententry', 'editpatiententry', 'deletepatiententry'] }
  ],
  // Delivery is a separate category in backend
  delivery: [
    { name: 'Delivery', permissions: ['viewdelivery', 'adddelivery', 'editdelivery', 'deletedelivery'] }
  ],
  report: [
    // Report: enable only Create and Delete checkboxes (no Edit)
    // Column order: [View, Create, Edit, Delete]
    { name: 'Patient', permissions: ['viewreport', 'addreport', '-', 'deletereport'] }
  ],
  excel: [
    { name: 'Excel Export', permissions: ['excelexport', '-', '-', '-'] }
  ],
  // dont remove comment it -> lead permission is now active
  lead: [
    { name: 'Lead', permissions: ['-', '-', '-', 'deleteview'] }
  ],
  invoice: [
    { name: 'Invoice', permissions: ['-', '-', 'invoiceeditview', 'invoicedeleteview'] }
  ]
};

const EditUserPage = () => {
  const { user_id } = useParams();

  const [userData, setUserData] = useState({
    user_type: '',
    user_name: '',
    password: '',
    user_branch: ''
  });

  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [branches, setBranches] = useState([]);

  // Store original user data and permissions to compare for changes
  const originalUserDataRef = useRef(null);
  const originalPermissionsRef = useRef(null);

  // Fetch user data
  useEffect(() => {
    if (!user_id) {
      setError('No user ID provided');
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem('session_token');
        const url = `https://newerp.awasthivitality.com/api/user/edit/${user_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || 'Failed to fetch user data');
        }

        const user = result.data || {};

        // Set user data
        setUserData({
          user_type: user.user_type || '',
          user_name: user.user_name || '',
          password: '',
          user_branch: user.user_branch || ''   // 👈 IMPORTANT
        });

        // Store original user data for comparison
        originalUserDataRef.current = {
          user_type: user.user_type || '',
          user_name: user.user_name || '',
          // password CAN'T be compared
          user_branch: user.user_branch || ''
        };

        // Parse permissions - API returns decoded JSON as array
        let userPermissions = {};
        if (user.permission) {
          try {
            // Permission is already decoded by the API
            userPermissions = user.permission;
          } catch (e) {
            userPermissions = {};
          }
        }

        // Convert API permissions to component state
        const componentPermissions = {};

        Object.entries(permissionStructure).forEach(([section, items]) => {
          items.forEach(item => {
            const key = `${section}-${item.name}`;
            componentPermissions[key] = {};
            const sectionPermissions = userPermissions[section] || [];
            item.permissions.forEach(perm => {
              if (perm !== '-') {
                const isChecked = Array.isArray(sectionPermissions) && sectionPermissions.includes(perm);
                componentPermissions[key][perm] = isChecked;
              }
            });
          });
        });

        // ✅ FORCE INIT FOR INVOICE and ALL permissions (no undefined keys for checkbox values!)
        Object.keys(permissionStructure).forEach(section => {
          permissionStructure[section].forEach(item => {
            const key = `${section}-${item.name}`;
            if (!componentPermissions[key]) {
              componentPermissions[key] = {};
            }
            item.permissions.forEach(perm => {
              if (perm !== '-' && componentPermissions[key][perm] === undefined) {
                componentPermissions[key][perm] = false;
              }
            });
          });
        });

        // 🔥 FORCE invoice init (MANDATORY)
        const invoiceKey = 'invoice-Invoice';
        if (!componentPermissions[invoiceKey]) {
          componentPermissions[invoiceKey] = {
            invoiceeditview: false,
            invoicedeleteview: false
          };
        } else {
          if (componentPermissions[invoiceKey].invoiceeditview === undefined) {
            componentPermissions[invoiceKey].invoiceeditview = false;
          }
          if (componentPermissions[invoiceKey].invoicedeleteview === undefined) {
            componentPermissions[invoiceKey].invoicedeleteview = false;
          }
        }

        // Save the original permissions for comparing later
        originalPermissionsRef.current = JSON.parse(JSON.stringify(componentPermissions));
        setPermissions(componentPermissions);
        setLoading(false);

      } catch (err) {
        setError(err.message);
        setLoading(false);
        toast.error(err.message || 'Failed to load user data.');
      }
    };

    fetchUserData();
  }, [user_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
  };

  const getAuthToken = () => localStorage.getItem('session_token');

  const fetchBranches = async () => {
    try {
      const token = getAuthToken();
      const url = token
        ? `https://newerp.awasthivitality.com/api/branch/show?token=${encodeURIComponent(token)}`
        : 'https://newerp.awasthivitality.com/api/branch/show';

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (result.success) {
        setBranches(
          result.data.map(branch => ({
            id: branch.branch_id,
            name: branch.branch_name,
          }))
        );
      }
    } catch (err) {
      // not critical
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handlePermissionChange = (section, item, permission) => {
    // ✅ Debug: See if checkboxes actually fire
    console.log(section, item, permission);
    setPermissions(prev => {
      const key = `${section}-${item}`;
      const current = prev[key] || {};
      const currentValue = current[permission] || false;
      const newValue = !currentValue;
      return {
        ...prev,
        [key]: { ...current, [permission]: newValue }
      };
    });
  };

  const renderPermissionRow = (section, item) => {
    const name = item.name;
    const perms = item.permissions;
    const key = `${section}-${name}`;
    const currentPermissions = permissions[key] || {};

    return (
      <tr key={key}>
        <td><b>{name}</b></td>
        {perms.map((perm, idx) => {
          const isChecked = currentPermissions[perm] === true;
          return (
            <td key={idx} className="text-center">
              {perm !== '-' ? (
                <input
                  type="checkbox"
                  className="edituser-checkbox"
                  checked={isChecked}
                  onChange={() => handlePermissionChange(section, name, perm)}
                  disabled={saving}
                />
              ) : (
                <span>-</span>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // Helper: Deep permission comparison
  function arePermissionsEqual(a, b) {
    if (!a || !b) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (!b.hasOwnProperty(key)) return false;
      const aPerms = a[key];
      const bPerms = b[key];
      const aPermKeys = Object.keys(aPerms);
      const bPermKeys = Object.keys(bPerms);
      if (aPermKeys.length !== bPermKeys.length) return false;
      for (const perm of aPermKeys) {
        if (aPerms[perm] !== bPerms[perm]) return false;
      }
    }
    return true;
  }

  // Helper: For user data (excluding password)
  function isUserDataChanged(current, original) {
    if (!original) return true;
    for (const k of ['user_type', 'user_name', 'user_branch']) {
      if ((current[k] ?? "") !== (original[k] ?? "")) return true;
    }
    return false;
  }

  // dont remove comment it -> function convertPermissionsForApi(permissions) {
  function convertPermissionsForApi(permissions) {
    const apiPermissions = {
      profile: [],
      folders: [],
      master: [],
      operation: [],
      delivery: [],
      report: [],
      excel: [],
      lead: [],
      invoice: []
    };

    Object.entries(permissionStructure).forEach(([section, items]) => {
      items.forEach(item => {
        const key = `${section}-${item.name}`;
        const perms = permissions[key] || {};

        item.permissions.forEach(perm => {
          if (perm !== '-' && perms[perm] === true) {
            apiPermissions[section].push(perm);
          }
        });
      });
    });

    // 🔥 CRITICAL FIX: force invoice array (backend requires it)
    if (!apiPermissions.invoice || apiPermissions.invoice.length === 0) {
      const invoiceKey = 'invoice-Invoice';
      const inv = permissions[invoiceKey] || {};

      apiPermissions.invoice = [];

      if (inv.invoiceeditview) {
        apiPermissions.invoice.push('invoiceeditview');
      }
      if (inv.invoicedeleteview) {
        apiPermissions.invoice.push('invoicedeleteview');
      }
    }

    console.log("🔥 FINAL SENDING:", apiPermissions);

    return apiPermissions;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      // ✅ Debug: Log full permission state each time before sending to API!
      console.log("🔥 PERMISSION STATE:", permissions);
      console.log("🔥 INVOICE STATE:", permissions['invoice-Invoice']);

      // Convert component permissions back to API format
      const apiPermissions = convertPermissionsForApi(permissions);

      // Determine if permission changes
      const originalPermissionsObj = originalPermissionsRef.current;
      const originalUserData = originalUserDataRef.current;
      const changedPermissions = !arePermissionsEqual(permissions, originalPermissionsObj);
      const changedUserData = isUserDataChanged(userData, originalUserData);
      const isPasswordChanged = !!(userData.password && userData.password.trim());

      if (!changedUserData && !changedPermissions && !isPasswordChanged) {
        toast.info('No changes detected, but update will be forced.');
      }

      const updateData = {
        user_type: userData.user_type,
        user_name: userData.user_name,
        user_branch: userData.user_branch,
        permission: apiPermissions
      };

      if (isPasswordChanged) {
        updateData.password = userData.password;
      }

      const token = localStorage.getItem('session_token');
      const url = `https://newerp.awasthivitality.com/api/user/update/${user_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;

      // Try PUT first with auth headers; then fallback to POST + _method=PUT for servers blocking PUT
      const tryRequests = async () => {
        let resp = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(token ? { 'X-Session-Token': token } : {}),
          },
          body: JSON.stringify(updateData),
        });
        if (resp.ok) return resp;

        // fallback for _method=PUT
        const u2 = new URL(url);
        u2.searchParams.set('_method', 'PUT');
        resp = await fetch(u2.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(token ? { 'X-Session-Token': token } : {}),
          },
          body: JSON.stringify(updateData),
        });
        return resp;
      };

      const response = await tryRequests();
      const result = await response.json().catch(() => ({}));

      if (response.ok && (result.success !== false)) {
        toast.success(result.message || 'User updated successfully!');
        clearPermissionCache();
        window.dispatchEvent(new CustomEvent('permissionsUpdated'));

        // Re-sync original state with updated data
        if (result.data && result.data.permission) {
          const savedPerms = result.data.permission;
          const componentPerms = {};
          Object.entries(permissionStructure).forEach(([section, items]) => {
            items.forEach(item => {
              const key = `${section}-${item.name}`;
              componentPerms[key] = {};
              const sectionPermissions = savedPerms[section] || [];
              item.permissions.forEach(perm => {
                if (perm !== '-') {
                  componentPerms[key][perm] = Array.isArray(sectionPermissions) && sectionPermissions.includes(perm);
                }
              });
            });
          });

          // ✅ FORCE INIT FOR ALL CHECKBOX KEYS
          Object.keys(permissionStructure).forEach(section => {
            permissionStructure[section].forEach(item => {
              const key = `${section}-${item.name}`;
              if (!componentPerms[key]) {
                componentPerms[key] = {};
              }
              item.permissions.forEach(perm => {
                if (perm !== '-' && componentPerms[key][perm] === undefined) {
                  componentPerms[key][perm] = false;
                }
              });
            });
          });

          // 🔥 FORCE invoice init (MANDATORY)
          const invoiceKey = 'invoice-Invoice';
          if (!componentPerms[invoiceKey]) {
            componentPerms[invoiceKey] = {
              invoiceeditview: false,
              invoicedeleteview: false
            };
          } else {
            if (componentPerms[invoiceKey].invoiceeditview === undefined) {
              componentPerms[invoiceKey].invoiceeditview = false;
            }
            if (componentPerms[invoiceKey].invoicedeleteview === undefined) {
              componentPerms[invoiceKey].invoicedeleteview = false;
            }
          }

          setPermissions(componentPerms);
          originalPermissionsRef.current = JSON.parse(JSON.stringify(componentPerms));
        }
        setUserData(prev => ({ ...prev, password: '' }));
        // Also update originalUserDataRef after a successful update (excluding password)
        originalUserDataRef.current = {
          user_type: userData.user_type,
          user_name: userData.user_name,
          user_branch: userData.user_branch
        };
      } else {
        throw new Error(result.message || result.msg || `Update failed (HTTP ${response.status})`);
      }

    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Something went wrong!');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edituser-container">
        <div className="loading-message">Loading user data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edituser-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="edituser-container">
      <h2>Edit User</h2>
      <ToastContainer position="top-right" autoClose={3000} />

      <form onSubmit={handleSubmit}>
        <div className="edituser-form-section">
          <div>
            <label className="edituser-label">User Type <span className="edituser-required">*</span></label>
            <select
              name="user_type"
              value={userData.user_type}
              onChange={handleInputChange}
              className="edituser-select"
              required
              disabled={saving}
            >
              <option value="">--Select--</option>
              <option value="USER">User</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          <div>
            <label className="user-add-label">
              User Branch <span className="user-add-required">*</span>
            </label>
            <select
              name="user_branch"
              value={String(userData.user_branch)}
              onChange={handleInputChange}
              className="user-add-select"
              required
              disabled={saving}
            >
              {branches.map(branch => (
                <option key={branch.id} value={String(branch.id)}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="edituser-label">Username <span className="edituser-required">*</span></label>
            <input
              type="text"
              name="user_name"
              value={userData.user_name}
              onChange={handleInputChange}
              className="edituser-input"
              required
              disabled={saving}
            />
          </div>

          <div>
            <label className="edituser-label">Password</label>
            <input
              type="password"
              name="password"
              value={userData.password}
              onChange={handleInputChange}
              className="edituser-input"
              placeholder="Leave blank to keep current password"
              disabled={saving}
            />
          </div>
        </div>

        <div className="edituser-permissions-section">
          {Object.entries(permissionStructure).map(([section, items]) => (
            <div key={section} className="edituser-permission-block">
              <h3>{section.charAt(0).toUpperCase() + section.slice(1)}</h3>
              <table className="edituser-table">
                <thead>
                  <tr>
                    <th>Permissions</th>
                    <th>View</th>
                    <th>Create</th>
                    <th>Edit</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => renderPermissionRow(section, item))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <button
          type="submit"
          className="edituser-submit-btn"
          disabled={saving}
        >
          {saving ? 'Updating...' : 'Update'}
        </button>
      </form>
    </div>
  );
};

export default EditUserPage;